/**
 * THE WORKSPACE SWITCH IN THE NEW DESIGN (PV5-8:D part 2, 2026-10-13; definitions.md, the workspace switch, drafted
 * under D-87 until Miles rules it).
 *
 * The full-screen announcement every change of organization shows, and its continuation after the reload: the sky in
 * the visitor's light, opaque; the wordmark with the mark as its zero, drawn as and where the boot splash draws it (the
 * one inline mark, BrandMark, on the splash's canvas, with the dusk satin at dusk); the eyebrow, the
 * organization's name, the thread it opens and whose usage the work now counts toward; the working mark. No dismiss.
 *
 * It only draws. What is announced and when, and the seal (the scroll lock, the focus sink, every key swallowed), are
 * src/hooks/workspaceSwitch/useWorkspaceSwitch.ts, which today's overlay draws from too; its words are the words file's.
 * Its sheet travels with it (./workspaceSwitchCss.ts): it rides the first download, so it needs nothing that loads on
 * demand. The door (./switchDoor.ts, asked in src/components/WorkspaceTransitionOverlay.tsx) picks it and its light.
 */
import React from 'react';
import { BrandMark } from '../../../components/common/BrandMark';
import { MarkSpinner } from '../../../components/common/MarkSpinner';
import { SWITCH_WORDS, openingThread, organizationShown, usageBelongsTo } from '../../../components/workspaceSwitch/workspaceSwitchWords';
import { useSwitchSeal, type WorkspaceSwitchView } from '../../../hooks/workspaceSwitch/useWorkspaceSwitch';
import type { SwitchLight } from './switchLooks';
import { WORKSPACE_SWITCH_CSS } from './workspaceSwitchCss';

export interface PavilionWorkspaceSwitchProps {
  view: WorkspaceSwitchView;
  /** The boot continuation is fading out. */
  leaving?: boolean;
  light: SwitchLight;
}

export const PavilionWorkspaceSwitch: React.FC<PavilionWorkspaceSwitchProps> = ({ view, leaving, light }) => {
  const sinkRef = useSwitchSeal();
  const orgDisplay = organizationShown(view.label);
  return (
    <div
      className="pv-ws"
      data-light={light}
      data-leaving={leaving ? '' : undefined}
      data-workspace-transition-overlay=""
      data-prest0n-portal="true"
      role="status"
      aria-live="polite"
      aria-busy={view.kind !== 'error'}
    >
      <style>{WORKSPACE_SWITCH_CSS}</style>
      <div className="pv-ws-card" ref={sinkRef} tabIndex={-1}>
        <span className="pv-ws-wordmark">
          prest
          <BrandMark className="pv-ws-zero" label="0" dusk={light === 'dusk'} />
        </span>
        {view.kind === 'error' ? (
          <>
            <div className="pv-ws-eyebrow">{SWITCH_WORDS.failedTitle}</div>
            <div className="pv-ws-failed" data-workspace-transition-error="">
              {SWITCH_WORDS.failedLine}
            </div>
          </>
        ) : (
          <>
            <div className="pv-ws-eyebrow">{view.kind === 'arriving' ? SWITCH_WORDS.arriving : SWITCH_WORDS.switching}</div>
            <div className="pv-ws-org" data-workspace-transition-org="">{orgDisplay}</div>
            {view.threadTitle && (
              <div className="pv-ws-thread" data-workspace-transition-thread="">
                {openingThread(view.threadTitle)}
              </div>
            )}
            <div className="pv-ws-usage" data-workspace-transition-usage="">
              {usageBelongsTo(orgDisplay)}
            </div>
            <MarkSpinner size="xl" className="pv-ws-spin" />
          </>
        )}
      </div>
    </div>
  );
};

export default PavilionWorkspaceSwitch;
